// src/components/games/dungeon/dungeonHints.ts

import { Character, GameState, Room } from './dungeonTypes';
import { hasScrolls } from './dungeonUtils';

export function getHint(
  char: Character,
  gs: GameState,
  dungeon: { [key: string]: Room }
): string {
  if (!dungeon["attic room"].v && !dungeon["east room"].v) {
    return "Start exploring! Try moving to another room and seeing what's there. You can move by typing directions like 'east', 'west', 'up', or 'down'.";
  }

  if (dungeon["attic room"].v && !dungeon["east room"].v) {
    return "You've explored upward, but maybe you missed something on the main floor. Try going back down to the center room and exploring east.";
  }

  if (dungeon["east room"].v && !gs.braz) {
    return "The blazing fire in the east room is too bright to examine the brazier properly. You'll need to extinguish it somehow.";
  }

  if (dungeon["attic room"].v && !dungeon["roof"].v && !dungeon["attic room"].e.south) {
    return "Try exploring all directions from the attic room. Some passages might not be obvious at first glance.";
  }

  if (!dungeon["library"].v) {
    return "Continue exploring from the attic room. There are more areas to discover beyond what you've seen so far.";
  }

  if (!gs.mag && char.inv.includes("quill pen") && hasScrolls(char.inv)) {
    return "The ancient tome in the library contains mysterious symbols. Perhaps you could copy them onto something with your writing materials.";
  }

  if (!gs.mag && !char.inv.includes("quill pen")) {
    return "You'll need writing materials to make progress. Look for a quill pen and paper in the library.";
  }

  if (gs.mag && !gs.trap && char.inv.includes("magic scroll")) {
    return "Your magic scroll pulses with energy. Try using it in the center room where the fungi glow overhead.";
  }

  if (gs.braz && char.inv.includes("incomplete star chart") && char.inv.includes("quill pen")) {
    return "Now that the brazier is extinguished, you can see star patterns carved into its surface. Compare these with your incomplete star chart.";
  }

  if (char.inv.includes("complete star chart") && dungeon["observatory"].v) {
    return "Your complete star chart shows the constellation. Try using it with the telescope in the observatory.";
  }

  if (char.inv.includes("crowbar") && !dungeon["attic room"].e.south) {
    return "The locked window in the attic looks like it could be forced open with the right tool.";
  }

  if (gs.tel && char.inv.includes("shiny gem") && dungeon["roof"].v) {
    return "The gargoyle on the roof seems interested in shiny objects. Try offering it your gem.";
  }

  if (gs.braz && char.inv.includes("shiny gem") && !dungeon["roof"].v) {
    return "You found something shiny in the brazier ashes, but you'll need to find a way to the roof. Try breaking the attic window.";
  }

  if (!gs.mech && dungeon["basement"].v) {
    if (!gs.gear) {
      return "The ancient mechanism in the basement is missing parts. Look for a gear and lever that might fit - but choose carefully.";
    } else {
      return "You've installed a gear in the mechanism. Now you need the right lever to activate it.";
    }
  }

  if (gs.mech && dungeon["crypt"].v && !gs.cry) {
    if (char.inv.includes("crystal lens") && char.inv.includes("ornate glass mirror")) {
      return "The reflecting pool in the crypt glows with light. Try focusing light from the pool onto the murals using your optical items.";
    } else {
      return "This crypt needs light to reveal its secrets. You'll need items that can focus and direct light.";
    }
  }

  if (gs.cry && dungeon["underground lake"].v && !gs.lake) {
    if (char.inv.includes("silver statue") && char.inv.includes("spiral hourglass")) {
      return "The altar has two distinct indentations. Try placing both your special items in their matching slots.";
    } else if (char.inv.includes("silver statue")) {
      return "You have one item for the altar, but you'll need something else - perhaps an hourglass.";
    } else if (char.inv.includes("spiral hourglass")) {
      return "You have one item for the altar, but you'll need something else - perhaps a small statue.";
    } else {
      return "The altar requires two specific items. Look for a small statue and an unusual hourglass.";
    }
  }

  if (gs.lake && dungeon["lake chamber"].v && !gs.ciph) {
    if (!char.inv.includes("crystal orb") && !gs.puzz) {
      return "The lake chamber holds secrets, but you'll need to solve the puzzle in the hidden chamber first.";
    } else if (char.inv.includes("crystal orb") && !dungeon["lake chamber"].c.includes("silver emblem")) {
      return "Try using your crystal orb between the different pillars to see if it reveals any hidden secrets.";
    } else if (char.inv.includes("deciphered scroll")) {
      return "You've decoded the ancient message. Try speaking the words at the altar in the underground lake.";
    } else if (char.inv.includes("cipher scroll")) {
      return "Take the cipher scroll to the library to research their meaning in the ancient tomes.";
    } else if (char.inv.includes("quill pen") && hasScrolls(char.inv)) {
      return "The pillars bear mysterious cipher symbols. Copy them down with your writing materials.";
    } else {
      return "The lake chamber pillars show cryptic symbols that need to be studied.";
    }
  }

  if (gs.ciph && char.inv.includes("ancient coin") && char.inv.includes("golden key") && char.inv.includes("silver emblem") && dungeon["secret"].v) {
    return "You have all three keys for the final chest. Use the golden key to begin the unlocking sequence.";
  } else if (dungeon["secret"].v) {
    const missing = [];
    if (!char.inv.includes("golden key")) missing.push("golden key (from the gargoyle)");
    if (!char.inv.includes("ancient coin")) missing.push("ancient coin (from the altar puzzle)");
    if (!char.inv.includes("silver emblem")) missing.push("silver emblem (from the lake chamber)");
    return "The final chest needs three keys. You're missing: " + missing.join(", ") + ".";
  }

  if (!dungeon["secret"].v && dungeon["closet"].v) {
    return "There's mysterious fog in the closet flowing from the east. Try moving deeper into the closet in that direction.";
  }

  if (gs.fin) {
    return "Congratulations! You've completed the adventure and opened the escape portal. Where it leads... that's another adventure entirely!";
  }

  return "Continue exploring and experimenting with the items you've found. Look for clues in room descriptions.";
}